import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './MainMediaGallery.css';

const MainMediaGallery = ({ mediaData }) => {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openModal = (index) => {
    setCurrentIndex(index);
    setSelectedMedia(mediaData[index]);
  };

  const closeModal = () => {
    setSelectedMedia(null);
  };

  const nextMedia = () => {
    const nextIndex = (currentIndex + 1) % mediaData.length;
    setCurrentIndex(nextIndex);
    setSelectedMedia(mediaData[nextIndex]);
  };

  const prevMedia = () => {
    const prevIndex = (currentIndex - 1 + mediaData.length) % mediaData.length;
    setCurrentIndex(prevIndex);
    setSelectedMedia(mediaData[prevIndex]);
  };

  if (!mediaData || mediaData.length === 0) return null;

  return (
    <section id="media" className="media-section">
      <h2>Media Gallery</h2>
      <div className="media-grid">
        {mediaData.map((media, index) => (
          <motion.div
            key={index}
            className="media-item"
            onClick={() => openModal(index)}
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            {media.type === 'image' ? (
              <div className="image-item">
                <img 
                  src={media.thumbnail || media.src} 
                  alt={media.title || 'Media item'} 
                  loading="lazy"
                />
                <div className="media-overlay">
                  <i className="fas fa-search-plus"></i>
                  {media.title && <p>{media.title}</p>}
                </div>
              </div>
            ) : (
              <div className="video-item">
                <div className="video-thumbnail">
                  <img 
                    src={media.thumbnail} 
                    alt={media.title || 'Video thumbnail'} 
                    loading="lazy"
                  />
                  <div className="play-button">
                    <i className="fas fa-play"></i>
                  </div>
                </div>
                <div className="media-overlay">
                  <i className="fas fa-play"></i>
                  {media.title && <p>{media.title}</p>}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Media Modal */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div
            className="media-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="media-modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="media-modal-close" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>

              {mediaData.length > 1 && (
                <>
                  <button className="media-modal-nav prev" onClick={prevMedia}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button className="media-modal-nav next" onClick={nextMedia}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </>
              )}

              <div className="media-modal-body">
                {selectedMedia.type === 'image' ? (
                  <img 
                    src={selectedMedia.src} 
                    alt={selectedMedia.title || 'Media item'} 
                  />
                ) : (
                  // Check if it's a YouTube video
                  selectedMedia.src.includes('youtube.com') || selectedMedia.src.includes('youtu.be') ? (
                    <iframe
                      src={selectedMedia.src}
                      width="100%"
                      height="100%"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      title={selectedMedia.title || 'YouTube video'}
                    />
                  ) : selectedMedia.src.includes('drive.google.com') ? (
                    <iframe
                      src={`https://drive.google.com/file/d/${selectedMedia.src.split('id=')[1]}/preview`}
                      width="100%"
                      height="100%"
                      allow="autoplay"
                      allowFullScreen
                      title={selectedMedia.title || 'Video'}
                    />
                  ) : (
                    <video 
                      src={selectedMedia.src} 
                      controls 
                      autoPlay
                      poster={selectedMedia.thumbnail}
                    >
                      Your browser does not support the video tag.
                    </video>
                  )
                )}
              </div>

              <div className="media-modal-info">
                {selectedMedia.title && (
                  <h3 className="media-modal-title">{selectedMedia.title}</h3>
                )}
                {selectedMedia.description && (
                  <p className="media-modal-description">{selectedMedia.description}</p>
                )}
                {selectedMedia.caption && (
                  <p className="media-modal-caption">{selectedMedia.caption}</p>
                )}
              </div>

              {mediaData.length > 1 && (
                <div className="media-modal-counter">
                  {currentIndex + 1} / {mediaData.length}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default MainMediaGallery;
